export type AiSalesOutputKind = 'forecast' | 'fraud' | 'nextBest' | 'generic';

export interface AiSalesOutputField {
  labelKey: string;
  value: string;
}

export interface AiSalesOutputTable {
  headerKeys: string[];
  rows: string[][];
}

export interface AiSalesOutputBadge {
  labelKey: string;
  tone: 'info' | 'success' | 'warn' | 'danger';
}

export interface AiSalesOutputViewModel {
  kind: AiSalesOutputKind;
  summary?: string;
  fields: AiSalesOutputField[];
  table?: AiSalesOutputTable;
  badges: AiSalesOutputBadge[];
  showRaw: boolean;
  rawJson: string;
}

const emptyModel = (raw = ''): AiSalesOutputViewModel => ({
  kind: 'generic',
  fields: [],
  badges: [],
  showRaw: !!raw,
  rawJson: raw
});

export function parseSalesAiOutput(outputJson: string | null | undefined): AiSalesOutputViewModel {
  const raw = (outputJson ?? '').trim();
  if (!raw) return emptyModel();

  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return { ...emptyModel(raw), summary: raw };
  }

  if (!data || typeof data !== 'object') return { ...emptyModel(raw), summary: raw };

  const record = data as Record<string, unknown>;
  if (record['stub'] === true) {
    return {
      kind: 'generic',
      summary: 'sales.ai.output.stubEngine',
      fields: flattenGeneric(record).filter(f => f.labelKey !== 'sales.ai.output.fields.stub'),
      badges: [],
      showRaw: false,
      rawJson: raw
    };
  }

  if (isForecast(record)) return parseForecast(record, raw);
  if (isFraud(record)) return parseFraud(record, raw);
  if (isNextBest(record)) return parseNextBest(record, raw);

  return {
    kind: 'generic',
    fields: flattenGeneric(record),
    badges: [],
    showRaw: true,
    rawJson: raw
  };
}

export function parseSalesAiInputSnapshot(inputJson: string | null | undefined): AiSalesOutputViewModel {
  const raw = (inputJson ?? '').trim();
  if (!raw) return emptyModel();
  try {
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') return { ...emptyModel(raw), summary: raw };
    return {
      kind: 'generic',
      fields: flattenGeneric(data as Record<string, unknown>),
      badges: [],
      showRaw: true,
      rawJson: raw
    };
  } catch {
    return { ...emptyModel(raw), summary: raw };
  }
}

function isForecast(r: Record<string, unknown>): boolean {
  return Array.isArray(r['points']) || typeof r['horizonPeriods'] === 'number';
}

function isFraud(r: Record<string, unknown>): boolean {
  return Array.isArray(r['signals']) || typeof r['alert'] === 'boolean';
}

function isNextBest(r: Record<string, unknown>): boolean {
  return typeof r['action'] === 'string';
}

function parseForecast(r: Record<string, unknown>, raw: string): AiSalesOutputViewModel {
  const forecastType = asString(r['forecastType']) ?? '—';
  const horizon = asNumber(r['horizonPeriods']);
  const band = r['confidenceBand'] as Record<string, unknown> | undefined;
  const lower = band ? formatPercent(asNumber(band['lower'])) : null;
  const upper = band ? formatPercent(asNumber(band['upper'])) : null;

  const fields: AiSalesOutputField[] = [
    { labelKey: 'sales.ai.output.fields.forecastType', value: forecastType },
    ...(horizon !== null ? [{ labelKey: 'sales.ai.output.fields.horizonPeriods', value: String(horizon) }] : [])
  ];
  if (lower && upper) {
    fields.push({ labelKey: 'sales.ai.output.fields.confidenceBand', value: `${lower} – ${upper}` });
  }

  const points = Array.isArray(r['points']) ? r['points'] : [];
  const rows = points.map(p => {
    const pt = p as Record<string, unknown>;
    return [asString(pt['period']) ?? '—', formatNumber(asNumber(pt['value']))];
  });

  return {
    kind: 'forecast',
    fields,
    table: rows.length
      ? { headerKeys: ['sales.ai.output.columns.period', 'sales.ai.output.columns.value'], rows }
      : undefined,
    badges: [],
    showRaw: rows.length === 0,
    rawJson: raw
  };
}

function parseFraud(r: Record<string, unknown>, raw: string): AiSalesOutputViewModel {
  const alert = r['alert'] === true;
  const autoBlock = r['autoBlock'] === true;
  const threshold = formatPercent(asNumber(r['threshold']));

  const badges: AiSalesOutputBadge[] = [
    {
      labelKey: alert ? 'sales.ai.output.badges.fraudAlert' : 'sales.ai.output.badges.fraudClear',
      tone: alert ? 'warn' : 'success'
    },
    {
      labelKey: autoBlock ? 'sales.ai.output.badges.autoBlockOn' : 'sales.ai.output.badges.autoBlockOff',
      tone: autoBlock ? 'danger' : 'info'
    }
  ];

  const fields: AiSalesOutputField[] = [];
  if (threshold) fields.push({ labelKey: 'sales.ai.output.fields.threshold', value: threshold });

  const signals = Array.isArray(r['signals']) ? r['signals'] : [];
  const rows = signals.map(s => {
    const sig = s as Record<string, unknown>;
    const code = asString(sig['code']) ?? '—';
    const score = formatPercent(asNumber(sig['score'])) ?? '—';
    const message = asString(sig['message']) ?? '—';
    return [code, score, message];
  });

  return {
    kind: 'fraud',
    fields,
    table: rows.length
      ? {
          headerKeys: [
            'sales.ai.output.columns.signalCode',
            'sales.ai.output.columns.signalScore',
            'sales.ai.output.columns.signalMessage'
          ],
          rows
        }
      : undefined,
    badges,
    showRaw: false,
    rawJson: raw
  };
}

function parseNextBest(r: Record<string, unknown>, raw: string): AiSalesOutputViewModel {
  const action = asString(r['action']) ?? '—';
  const winProb = formatPercent(asNumber(r['winProbability']));
  const entityType = asString(r['entityType']);
  const entityId = asString(r['entityId']);

  const fields: AiSalesOutputField[] = [
    { labelKey: 'sales.ai.output.fields.suggestedAction', value: action },
    ...(winProb ? [{ labelKey: 'sales.ai.output.fields.winProbability', value: winProb }] : []),
    ...(entityType ? [{ labelKey: 'sales.ai.fields.entityType', value: entityType }] : []),
    ...(entityId ? [{ labelKey: 'sales.ai.output.fields.entityId', value: entityId }] : [])
  ];

  return {
    kind: 'nextBest',
    fields,
    badges: [],
    showRaw: false,
    rawJson: raw
  };
}

function flattenGeneric(r: Record<string, unknown>): AiSalesOutputField[] {
  return Object.entries(r)
    .filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object')
    .map(([key, value]) => ({
      labelKey: `sales.ai.output.fields.${key}`,
      value: String(value)
    }));
}

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

function asNumber(v: unknown): number | null {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && v.trim() && !Number.isNaN(Number(v))) return Number(v);
  return null;
}

function formatNumber(n: number | null): string {
  if (n === null) return '—';
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n);
}

function formatPercent(n: number | null): string | null {
  if (n === null) return null;
  const pct = n <= 1 ? n * 100 : n;
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(pct)}%`;
}
